import { Columns, Grid } from '@core-ds/icons/16';
import { breakpoint, color, fontSize, space } from '@core-ds/primitives';
import { _js } from '@ifixit/localize';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faMagnifyingGlass';
import Fuse from 'fuse.js';
import debounce from 'lodash/debounce';
import inRange from 'lodash/inRange';
import minBy from 'lodash/minBy';
import React, { Component } from 'react';
import smoothscroll from 'smoothscroll-polyfill';
import styled from 'styled-components';
import { Button } from './Button';
import Banner from './Banner';
import Breadcrumbs from './Breadcrumbs';
import ColumnExplorer from './ColumnExplorer';
import GridExplorer from './GridExplorer';
import NoResults from './NoResults';
import Spinner from './Spinner';
import {
   View,
   DevicePickerContextType,
   DevicePickerProps,
   SEARCH_INACTIVE,
   SEARCH_NO_RESULTS,
   SEARCH_PENDING,
   HIDE_GRID_VIEW,
} from './types';
import { above } from './utils/mediaQuery';
import { DevicePickerContext } from './DevicePickerContext';

smoothscroll.polyfill();

export const Container = styled.div`
   display: flex;
   flex-direction: column;
   height: 100%;
   color: ${color.gray[800]};
   background-color: ${color.gray[50]};
`;

export const SearchContainer = styled.div`
   display: flex;
   flex: 0 0 auto;
   align-items: center;
   padding-left: 15px;
   border-bottom: 1px solid ${color.gray[200]};
`;

export const SearchInput = styled.input`
   flex: 1 1 auto;
   margin-bottom: 0px;
   padding: ${space[4]};
   font-family: inherit;
   font-size: ${fontSize.xl};
   color: inherit;
   background-color: transparent;
   border: none;
   outline: 0px;

   &::placeholder {
      color: ${color.gray[400]};
   }

   ${above(breakpoint.sm)} {
      font-size: ${fontSize['2xl']};
   }
`;

const BannerContainer = styled.div`
   flex: 0 0 auto;
   order: 1;

   ${above(breakpoint.sm)} {
      order: 0;
   }
`;

const ButtonGroup = styled.div`
   margin-right: ${space[4]};
   display: flex;
`;

const IconButton = styled.div`
   margin: 0;
   padding: ${space[3]};
   cursor: hand;
`;

export const Toolbar = styled.div`
   flex: 0 0 auto;
   order: 1;
   display: flex;
   padding: ${space[3]};
   background-color: ${color.white};
   position: sticky;
   bottom: 0;
   margin-top: auto;

   ${above(breakpoint.sm)} {
      padding: ${space[4]};
      border-top: 1px solid ${color.gray[200]};
   }
`;

export const ToolbarRight = styled.div`
   margin-left: auto;
   display: flex;

   & > :not(:first-child) {
      margin-left: ${space[3]};
   }
`;

const SpinnerContainer = styled.div`
   flex: 1 1 100%;
   padding: ${space[4]};
   display: flex;
   flex-direction: column;
   justify-content: center;
   align-items: center;
   max-width: 100%;
`;

export class HierarchicalDevicePicker extends Component<DevicePickerProps> {
   context: any;
   translations: { [key: string]: string } = {};

   static defaultProps = {
      allowOrphan: false,
      objectName: 'device',
   };

   constructor(props: DevicePickerProps) {
      super(props);
   }

   componentDidMount() {
      const category = this.context.category || this.props.initialDevice;
      const loaded = this.context.loaded;

      if (this.context.query) {
         this.debouncedApplySearch();
      }

      if (category && loaded) {
         this.context.updateCategory(category);
      }

      // Start with the search input focused.
      this.searchInputRef.focus();

      window.addEventListener('keydown', this.handleKeyDown);
   }

   componentWillUnmount() {
      // Cancel any trailing calls to this debounced function.
      this.debouncedApplySearch.cancel();

      window.removeEventListener('keydown', this.handleKeyDown);
   }

   /**
    * Get item relative to an index given a distance.
    * @param {Object} params
    * @param {string[]} params.list
    * @param {number} params.index
    * @param {number} params.distance
    * @returns {string} - Item given distance away from given index.
    */
   getRelativeItem = ({
      list,
      index,
      distance,
   }: {
      list: string[];
      index: number;
      distance: number;
   }) => {
      let newIndex = (index + distance) % list.length;

      if (newIndex < 0) {
         newIndex += list.length;
      }

      return list[newIndex];
   };

   /**
    * Get a tree node given a path.
    * @param {Object} params
    * @param {Object} params.tree
    * @param {string[]} params.path
    * @returns {Object} - Tree node.
    */
   getNode = ({ tree, path }: { tree: any; path: string[] }): any => {
      if (path.length === 0) {
         return tree;
      }

      return this.getNode({
         tree: tree[path[0]],
         path: path.slice(1),
      });
   };

   /**
    * Update path and query state given a path.
    * @param {string[]} path
    * @param {boolean} updateQuery whether or not to update the query state
    */
   setPath = (path: string[], updateQuery = true) => {
      const {
         setCategory,
         setQuery,
         setPath,
         setSearchState,
      }: DevicePickerContextType = this.context;
      const displayTitle = this.getDisplayTitleFromItemName(
         path[path.length - 1] || '',
      );

      setCategory(displayTitle);

      if (updateQuery) {
         setQuery(displayTitle);
      }

      setSearchState(SEARCH_INACTIVE);
      setPath(path);
   };

   /**
    * Store reference to the search input DOM element.
    * @param {HTMLElement} element
    */
   searchInputRef: any = null;
   setSearchInputRef = (element: HTMLElement | null) => {
      this.searchInputRef = element;
   };

   /**
    * Determine whether or not a submit action should be allowed.
    * @returns {boolean}
    */
   allowSubmit = () =>
      this.context.path.length !== 0 &&
      this.context.searchState !== SEARCH_NO_RESULTS;

   /**
    * Handle all KeyDown events.
    * Calls the appropriate event handler based on which key was pressed.
    * @param {KeyboardEvent} event
    */
   handleKeyDown = (event: KeyboardEvent) => {
      switch (event.keyCode) {
         // Enter
         case 13:
            this.handleEnter(event);
            break;

         default:
            if (
               // if key is a-z, or ...
               (inRange(event.keyCode, 65, 91) ||
                  // if key is A-Z, or ...
                  inRange(event.keyCode, 48, 58) ||
                  // if key is backspace
                  event.keyCode === 8) &&
               // and the search input is not focused
               this.searchInputRef !== document.activeElement
            ) {
               this.searchInputRef.focus();
            }
      }
   };

   handleEnter = (event: KeyboardEvent) => {
      if (this.allowSubmit()) {
         const { path } = this.context;
         this.props.onSubmit(path[path.length - 1]);
         event.preventDefault();
      }
   };

   /**
    * Handle search input change event.
    * @param {InputEvent} event
    */
   handleSearchChange = (event: any) => {
      const { setQuery, setPath, setSearchState } = this.context;
      // Don't search if the string is all whitespace.
      const isSearching = !/^\s*$/.test(event.target.value);
      // Reset the device picker if the search text gets deleted.
      const path = isSearching ? this.context.path : [];

      if (event.target.value) {
         this.props.onSearch && this.props.onSearch(event.target.value);
      }

      setQuery(event.target.value);
      setSearchState(isSearching ? SEARCH_PENDING : SEARCH_INACTIVE);
      setPath(path);
      this.debouncedApplySearch();
   };

   getDisplayTitleFromItemName = (itemName: string): string => {
      return this.context.displayTitles[itemName] || itemName;
   };

   /**
    * Returns a flat list of [{
    *   itemName,
    *   path,
    * }, ...]
    */
   // from the default tree structure.
   createItemList = (
      tree: any,
      itemName: string | null = null,
      path: string[] = [],
   ): any => {
      const displayTitle = itemName
         ? this.getDisplayTitleFromItemName(itemName)
         : null;

      const item = {
         itemName,
         displayTitle,
         path,
         pathText: path.map(this.getDisplayTitleFromItemName).join(' '),
      };

      if (!tree) {
         // This is a leaf.
         return item;
      }

      const itemList = [];

      if (itemName) {
         // If this isn't the root node, include it in the list.
         itemList.push(item);
      }

      // Recursively create a list of items for each child.
      const childItems = Object.keys(tree).map((itemName) => {
         const childTree = tree[itemName];
         const childPath = [...path, itemName];
         return this.createItemList(childTree, itemName, childPath);
      });

      // Flatten the lists together.
      return itemList.concat(...childItems);
   };

   /**
    * Uses the current query to set the selected path.
    */
   itemList: any;
   applySearch = () => {
      if (this.context.searchState !== SEARCH_PENDING) {
         return;
      }

      if (!this.context.hierarchy) {
         // Don't generate the item list before hierarchy is ready
         return;
      }
      // Creating a flat list from the tree is expensive, so save the result.
      this.itemList =
         this.itemList || this.createItemList(this.context.hierarchy);

      const fuse = new Fuse<{
         itemName: string;
         displayTitle: string;
         path: string[];
         pathText: string;
      }>(this.itemList, {
         keys: [
            {
               name: 'itemName',
               weight: 0.3,
            },
            {
               name: 'pathText',
               weight: 0.7,
            },
            {
               name: 'displayTitle',
               weight: 0.3,
            },
         ],
         includeScore: true,
         threshold: 0.4,
      });
      const results = fuse.search(this.context.query.trim());

      if (results.length > 0) {
         const bestResult = minBy(results, (result) => {
            if (!result || result.score === undefined) {
               throw new Error('The fuse.js result was empty/wrong type.');
            }
            // Prefer more general categories (which have a smaller path length).
            return result.score * (1 + 0.1 * result.item.path.length);
         });
         this.context.setSearchState(SEARCH_INACTIVE);
         this.context.setPath(bestResult ? bestResult.item.path : []);
      } else {
         this.context.setSearchState(SEARCH_NO_RESULTS);
         this.context.setPath([]);
      }
   };

   /**
    * Debounced version of applySearch, so that the user can finish typing before
    * the UI jumps around.
    */
   debouncedApplySearch = debounce(this.applySearch, 500);

   render() {
      const {
         path,
         query,
         hierarchy,
         displayTitles,
         searchState,
         view,
         loaded,
      } = this.context;
      const {
         fetchChildren,
         onSubmit,
         onCancel,
         allowOrphan = HierarchicalDevicePicker.defaultProps.allowOrphan,
         objectName = HierarchicalDevicePicker.defaultProps.objectName,
      } = this.props;

      return (
         <Container data-reactroot id="devicePickerComponent">
            <SearchContainer>
               <FontAwesomeIcon
                  icon={faMagnifyingGlass}
                  color={color.gray[400]}
                  className="fa-abs-25"
               />
               <SearchInput
                  ref={this.setSearchInputRef}
                  placeholder={_js('Search')}
                  value={query}
                  onChange={this.handleSearchChange}
                  onKeyDown={(event) =>
                     event.key === 'Enter' && this.applySearch()
                  }
                  id="devicePickerSearch"
               />
               {!HIDE_GRID_VIEW && (
                  <ButtonGroup>
                     <IconButton
                        onClick={() => this.context.setView(View.Grid)}
                     >
                        <Grid
                           color={
                              view === View.Grid
                                 ? color.gray[800]
                                 : color.gray[400]
                           }
                        />
                     </IconButton>
                     <IconButton
                        onClick={() => this.context.setView(View.Column)}
                     >
                        <Columns
                           color={
                              view === View.Column
                                 ? color.gray[800]
                                 : color.gray[400]
                           }
                        />
                     </IconButton>
                  </ButtonGroup>
               )}
            </SearchContainer>

            {query &&
               path.length > 0 &&
               query.trim().toLowerCase() !==
                  this.getDisplayTitleFromItemName(
                     path[path.length - 1],
                  ).toLowerCase() &&
               allowOrphan && (
                  <BannerContainer>
                     <Banner
                        callToAction={_js('Choose %1', `"${query}"`)}
                        onClick={() => onSubmit(query)}
                     >
                        {_js("Don't see what you're looking for?")}
                     </Banner>
                  </BannerContainer>
               )}

            {path.length > 0 ? (
               <Breadcrumbs
                  path={path}
                  getDisplayTitleFromItemName={this.getDisplayTitleFromItemName}
                  onChange={this.setPath}
               />
            ) : null}
            {searchState === SEARCH_NO_RESULTS ? (
               <NoResults
                  query={query}
                  allowOrphan={allowOrphan}
                  selectItem={onSubmit}
               />
            ) : loaded ? (
               view === View.Grid ? (
                  <GridExplorer
                     hierarchy={hierarchy}
                     displayTitles={displayTitles}
                     fetchChildren={fetchChildren}
                     path={path}
                     onChange={this.setPath}
                     onSubmit={onSubmit}
                  />
               ) : (
                  <ColumnExplorer
                     hierarchy={hierarchy}
                     displayTitles={displayTitles}
                     fetchChildren={fetchChildren}
                     path={path}
                     onChange={this.setPath}
                     onSubmit={onSubmit}
                  />
               )
            ) : (
               <SpinnerContainer>
                  <Spinner color={color.gray[400]} />
               </SpinnerContainer>
            )}

            <Toolbar>
               <ToolbarRight>
                  <Button onClick={onCancel}>{_js('Cancel')}</Button>
                  <Button
                     disabled={!this.allowSubmit()}
                     onClick={() => onSubmit(path[path.length - 1])}
                  >
                     {/* Translators: %1 -> the singular object name for objects; usually "Device" or "Category". */}
                     {_js('Choose %1', objectName)}
                  </Button>
               </ToolbarRight>
            </Toolbar>
         </Container>
      );
   }
}

HierarchicalDevicePicker.contextType = DevicePickerContext;

export default HierarchicalDevicePicker;
